/* ==========================================================================
   GYAA — global.js
   JS compartido, vanilla, sin dependencias. Cada sección con interacción
   propia carga además su gyaa-<nombre>.js.
   ========================================================================== */

(function () {
  'use strict';

  /**
   * Header sticky: por encima del hero va superpuesto y transparente (Figma),
   * y al bajar se re-ancla arriba como barra glass condensada para que el
   * carrito quede SIEMPRE visible. Sin GSAP a propósito: debe funcionar
   * también con prefers-reduced-motion (ahí el CSS anula la transición).
   */
  function initStickyHeader() {
    var header = document.querySelector('.gyaa-header');
    if (!header || header.dataset.stickyInit) return;
    header.dataset.stickyInit = '1';

    var THRESHOLD = 120; /* alto del header + margen */
    var stuck = false;
    var ticking = false;

    /* En móvil el header va en flujo: al fijarse hay que reservar su alto
       para que el contenido no pegue un salto. */
    function measure() {
      if (stuck) return;
      document.body.style.setProperty('--gyaa-header-h', header.offsetHeight + 'px');
    }

    function apply() {
      ticking = false;
      var shouldStick = window.scrollY > THRESHOLD;
      if (shouldStick === stuck) return;
      stuck = shouldStick;
      document.body.classList.toggle('gyaa-header-stuck', stuck);

      if (stuck) {
        header.classList.add('is-stuck');
        /* Doble rAF: la clase de posición debe pintar antes de animar el top. */
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            if (stuck) header.classList.add('is-shown');
          });
        });
      } else {
        header.classList.remove('is-shown');
        header.classList.remove('is-stuck');
      }
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(apply);
      },
      { passive: true }
    );

    window.addEventListener('resize', measure, { passive: true });

    measure();
    apply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyHeader);
  } else {
    initStickyHeader();
  }

  /* El editor de temas recarga la sección del header por su cuenta. */
  document.addEventListener('shopify:section:load', initStickyHeader);

  /**
   * Menú móvil (overlay a pantalla completa). Mismo patrón que gyaa-popup.js:
   * guarda el foco previo, cierra con X / Escape / clic en el fondo, y
   * bloquea el scroll del body mientras está abierto.
   */
  function initMobileMenu() {
    /* Desde el 1-sep hay DOS disparadores del menú: la hamburguesa de la
       cabecera (oculta en móvil, sigue en el DOM) y el «Más» de la barra de
       navegación inferior. Se escuchan todos y el aria-expanded se sincroniza
       en todos. */
    var toggles = Array.prototype.slice.call(document.querySelectorAll('[data-menu-toggle]'));
    var overlay = document.querySelector('[data-menu-overlay]');
    if (!toggles.length || !overlay || overlay.dataset.menuInit) return;
    overlay.dataset.menuInit = '1';

    var closeBtn = overlay.querySelector('[data-menu-close]');

    function expanded(valor) {
      toggles.forEach(function (t) { t.setAttribute('aria-expanded', valor); });
    }

    function open() {
      overlay.__prevFocus = document.activeElement;
      overlay.hidden = false;
      expanded('true');
      document.body.classList.add('gyaa-menu-open');
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      overlay.hidden = true;
      expanded('false');
      document.body.classList.remove('gyaa-menu-open');
      if (overlay.__prevFocus && overlay.__prevFocus.focus) overlay.__prevFocus.focus();
    }

    toggles.forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        if (overlay.hidden) open();
        else close();
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', close);

    /* Clic en el fondo (fuera del nav y de los extremos) = cerrar */
    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) close();
    });

    /*
     * Trampa de foco. El menu es aria-modal, asi que el Tab no puede salirse a la
     * pagina de detras: al llegar al ultimo elemento vuelve al primero y al reves.
     * La lista se calcula en cada pulsacion porque el selector de idioma solo
     * aparece si la tienda tiene mas de uno.
     */
    function focusables() {
      var sel = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
      return Array.prototype.filter.call(overlay.querySelectorAll(sel), function (el) {
        /* Lo que queda debajo de una pantalla de «Shop» (mega menú) está inert: fuera de la trampa. */
        if (el.closest('[inert]')) return false;
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
      });
    }

    document.addEventListener('keydown', function (event) {
      if (overlay.hidden) return;

      if (event.key === 'Escape') { close(); return; }

      if (event.key !== 'Tab') return;

      var items = focusables();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!overlay.contains(document.activeElement)) {
        /* Si el foco ya se habia escapado (p. ej. tras cerrar un submenu), se recupera. */
        event.preventDefault();
        first.focus();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMobileMenu);
  } else {
    initMobileMenu();
  }

  document.addEventListener('shopify:section:load', initMobileMenu);

  /**
   * Selector de idioma del header (escritorio).
   * El markup usa <localization-form> con la lista en `hidden`. Ese elemento
   * trae su JS en Dawn, pero este tema es a medida y no lo incluye: sin esto
   * el boton no abre nada. Los <button> de dentro ya son submit del form de
   * localizacion, asi que basta con mostrar y ocultar la lista.
   */
  function initLocaleSelector() {
    document.querySelectorAll('.gyaa-header__locale').forEach(function (wrap) {
      if (wrap.dataset.localeInit) return;
      wrap.dataset.localeInit = '1';

      var btn = wrap.querySelector('.gyaa-header__locale-btn');
      var list = wrap.querySelector('.gyaa-header__locale-list');
      if (!btn || !list) return;

      function close() {
        list.hidden = true;
        btn.setAttribute('aria-expanded', 'false');
      }

      function open() {
        list.hidden = false;
        btn.setAttribute('aria-expanded', 'true');
      }

      btn.addEventListener('click', function (event) {
        event.stopPropagation();
        if (list.hidden) open();
        else close();
      });

      /* Clic fuera del selector = cerrar */
      document.addEventListener('click', function (event) {
        if (!wrap.contains(event.target)) close();
      });

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && !list.hidden) {
          close();
          btn.focus();
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLocaleSelector);
  } else {
    initLocaleSelector();
  }

  document.addEventListener('shopify:section:load', initLocaleSelector);

  /**
   * Actualiza el contador del carrito en el header tras un add/change AJAX.
   * Lo usan la product card (Task 5) y la PDP (Task 10).
   */
  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  /**
   * Artículos de verdad en el carrito: excluye la línea del handling fee que
   * mete la app Upcharge (producto de tipo `mws_fee_generated`).
   */
  function cartProductCount(cart) {
    var n = 0;
    (cart.items || []).forEach(function (item) {
      if (item.product_type !== 'mws_fee_generated') n += item.quantity;
    });
    return n;
  }

  /** Refresca el contador leyendo /cart.js */
  function refreshCartCount() {
    return fetch(window.Shopify.routes.root + 'cart.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        updateCartCount(cartProductCount(cart));
        return cart;
      });
  }

  /**
   * Anuncia un mensaje en la region aria-live del layout.
   * Se limpia antes de escribir porque si el texto nuevo es identico al anterior
   * el lector de pantalla no lo repite.
   */
  function announce(message) {
    var region = document.querySelector('[data-gyaa-status]');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(function () { region.textContent = message; }, 60);
  }

  /**
   * "El punto rojo" (elegido por Robert, 1-sep-2026): al agregar al carrito,
   * un destello sale del botón pulsado y viaja en línea recta al carrito de la
   * cabecera, que rebota y suma. 450 ms. Con "reducir movimiento" activado no
   * hay vuelo ni rebote: solo se actualiza el número.
   */
  function vueloAlCarrito(origen) {
    var carrito = document.querySelector('.gyaa-header__cart');
    var badge = carrito && carrito.querySelector('[data-cart-count]');
    if (!carrito) return;

    var quieto = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var puedeAnimar = typeof carrito.animate === 'function';

    function rebote() {
      if (quieto || !puedeAnimar) return;
      carrito.animate(
        [
          { transform: 'scale(1)' },
          { transform: 'scale(1.3) rotate(-5deg)' },
          { transform: 'scale(0.94)' },
          { transform: 'scale(1)' }
        ],
        { duration: 420, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }
      );
      if (badge && !badge.hidden) {
        badge.animate(
          [{ transform: 'scale(0.5)' }, { transform: 'scale(1.35)' }, { transform: 'scale(1)' }],
          { duration: 320, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }
        );
      }
    }

    if (quieto || !puedeAnimar || !origen) { rebote(); return; }

    var o = origen.getBoundingClientRect();
    var d = carrito.getBoundingClientRect();
    /* Si el carrito quedó fuera de pantalla (cabecera aún sin fijar), no se vuela. */
    if (d.bottom < 0 || d.top > window.innerHeight) { rebote(); return; }

    var punto = document.createElement('span');
    punto.setAttribute('aria-hidden', 'true');
    punto.style.cssText =
      'position:fixed;z-index:200;width:12px;height:12px;border-radius:50%;' +
      'background:var(--red,#e31b23);box-shadow:0 0 10px rgba(227,27,35,.8);' +
      'pointer-events:none;left:0;top:0;will-change:transform;';
    document.body.appendChild(punto);

    var x0 = o.left + o.width / 2 - 6;
    var y0 = o.top + o.height / 2 - 6;
    var x1 = d.left + d.width / 2 - 6;
    var y1 = d.top + d.height / 2 - 6;

    var anim = punto.animate(
      [
        { transform: 'translate(' + x0 + 'px,' + y0 + 'px) scale(1)', opacity: 1 },
        { transform: 'translate(' + x1 + 'px,' + y1 + 'px) scale(0.5)', opacity: 0.85 }
      ],
      { duration: 450, easing: 'cubic-bezier(0.3, 0.1, 0.35, 1)' }
    );

    function fin() { punto.remove(); rebote(); }
    anim.finished.then(fin, fin);
  }

  /**
   * Transición "la foto se abre": la imagen de la tarjeta pulsada recibe el
   * view-transition-name que la conecta con la imagen grande de la ficha.
   * Se pone SOLO al hacer clic para que nunca haya dos elementos con el mismo
   * nombre en la página (eso anularía la transición).
   */
  document.addEventListener('click', function (event) {
    var enlace = event.target.closest && event.target.closest('.gyaa-card__media, .gyaa-card__title a');
    if (!enlace) return;
    var tarjeta = enlace.closest('.gyaa-card');
    var img = tarjeta && tarjeta.querySelector('.gyaa-card__media img');
    if (!img) return;
    document.querySelectorAll('[data-foto-viaja]').forEach(function (previa) {
      previa.style.viewTransitionName = '';
      previa.removeAttribute('data-foto-viaja');
    });
    img.style.viewTransitionName = 'foto-producto';
    img.setAttribute('data-foto-viaja', '');
  });

  window.GYAA = {
    updateCartCount: updateCartCount,
    refreshCartCount: refreshCartCount,
    cartProductCount: cartProductCount,
    announce: announce,
    vueloAlCarrito: vueloAlCarrito
  };
})();
